import { AuthUser, fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useRoutes } from 'react-router-dom';

import { publicRoutes } from './public';

import { CircularProgress, Grid } from '@mui/material';
import { Hub } from 'aws-amplify/utils';
import protectedRoutes from './protected';
import * as Sentry from '@sentry/react';
import { setUser } from '@/lib/sentryClient';

type LoadSessionOptions = {
  forceRefresh?: boolean;
};

export const AppRoutes = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  const [route, setRoute] = useState<
    {
      path: string;
      element: JSX.Element;
    }[]
  >(protectedRoutes);
  const element = useRoutes(isAuthenticated ? route : publicRoutes);

  const params = new URLSearchParams(window.location.search);
  if (params.has('mt')) {
    sessionStorage.setItem('mt', params.get('mt') || '');
  }

  const loadSession = useCallback(async (options?: LoadSessionOptions) => {
    const session = await fetchAuthSession(options);

    if (session.tokens != null) {
      if ('ReactNativeWebView' in window) {
        // ReactNativeWebViewの型が追加できないため一旦anyにしている
        window.ReactNativeWebView?.postMessage(JSON.stringify({ idToken: session.tokens.idToken?.toString() }));
      }

      setIsAuthenticated(true);
    } else {
      Sentry.setUser(null);
      setIsAuthenticated(false);
    }
    return session;
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const validateAuthenticated = () => {
    getCurrentUser()
      .then((user: AuthUser) => {
        if (user && isAuthenticated === false) {
          setUser(user.userId, user.username, user.signInDetails?.loginId);
          setIsAuthenticated(true);
        } else if (!user && isAuthenticated === true) {
          setIsAuthenticated(false);
        }
      })
      .catch((err) => {
        setIsAuthenticated(false);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    validateAuthenticated();
    Hub.listen('auth', ({ payload: { event } }) => {
      switch (event) {
        case 'signedOut':
          validateAuthenticated();
          navigate('/');
          break;
      }
    });
  }, []);

  useEffect(() => {
    if (window.ReactNativeWebView?.injectedObjectJson) {
      try {
        const parsedData =
          typeof window.ReactNativeWebView.injectedObjectJson() === 'string'
            ? JSON.parse(window.ReactNativeWebView.injectedObjectJson())
            : window.ReactNativeWebView.injectedObjectJson();
        window.injectedData = parsedData;
      } catch (error) {
        console.error('Failed to parse injectedObjectJson:', error);
      }
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'medireco',
      medireco: resolveTrafficSource()
    });
  }, []);

  const resolveTrafficSource = (): 'ios' | 'android' | 'line' | 'web' => {
    const fromLine = new URLSearchParams(window.location.search).get('utm_source') === 'line';
    const trafficSource = window.injectedData?.OSType || (fromLine ? 'line' : 'web');
    return trafficSource;
  };

  if (isLoading) {
    return (
      <Grid container justifyContent="center" alignItems="center" style={{ height: '100vh' }}>
        <Grid item xs={12} justifyContent="center" alignItems="center" sx={{ textAlign: 'center' }}>
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  return element;
};

export default AppRoutes;
